import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Calendar, Clock, Truck } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

const MAX_ATTEMPTS = 6;

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("order_id");
    const sessionId = params.get("session_id");

    // PayPal flow: order already captured, fetch by order_id
    if (orderId) {
      api.get(`/checkout/order/${orderId}`)
        .then((res) => {
          setOrder(res.data.order);
          setState(res.data.order.payment_status === "paid" ? "success" : "error");
          if (!cleared.current) { clearCart(); cleared.current = true; }
        })
        .catch(() => setState("error"));
      return;
    }

    if (!sessionId) {
      setState("error");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setOrder(res.data.order);
          setState("success");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (res.data.status === "expired") {
          setState("error");
          return;
        }
      } catch (e) {
        console.error(e);
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-20 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-cocoa" />
            <h1 className="mt-6 font-heading text-3xl font-black text-ink">Confirming your payment...</h1>
            <p className="mt-2 text-ashydark">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="payment-success" className="w-full">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-600" />
            <h1 className="mt-6 font-heading text-4xl font-black text-ink">Order Confirmed!</h1>
            <p className="mt-3 text-ashydark">
              Thank you{order?.customer?.name ? `, ${order.customer.name}` : ""}! Your cookies will be freshly baked and ready for pickup.
            </p>

            {order && (
              <div className="mt-8 rounded-3xl border border-ashy/30 bg-white p-6 text-left">
                <div className="flex flex-wrap gap-6 border-b border-ashy/20 pb-4">
                  {order.fulfillment === "shipping" ? (
                    <div className="flex items-start gap-2 text-sm" data-testid="success-shipping">
                      <Truck className="mt-0.5 h-4 w-4 text-cocoa" />
                      <div>
                        <p className="font-semibold text-ink">{order.shipping_label || "Shipping"}</p>
                        <p className="text-ashydark">
                          {order.customer.address_street}, {order.customer.address_city}, {order.customer.address_state} {order.customer.address_zip}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-cocoa" />
                        <span className="text-ink">{order.customer.pickup_date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-cocoa" />
                        <span className="text-ink">{order.customer.pickup_time}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-4 space-y-3">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <div>
                        <p className="font-semibold text-ink">{item.label}</p>
                        {item.flavors && item.flavors.length > 0 && <p className="text-ashydark">{item.flavors.join(", ")}</p>}
                      </div>
                      <span className="font-semibold text-cocoa">${item.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {order.fulfillment === "shipping" && order.shipping_cost > 0 && (
                  <div className="mt-3 flex justify-between border-t border-ashy/20 pt-3 text-sm text-ashydark">
                    <span>{order.shipping_label || "Shipping"}</span>
                    <span>${order.shipping_cost.toFixed(2)}</span>
                  </div>
                )}
                <div className="mt-3 flex justify-between border-t border-ashy/20 pt-3">
                  <span className="font-semibold text-ink">Total Paid</span>
                  <span className="font-heading text-2xl font-black text-cocoa">${order.total.toFixed(2)}</span>
                </div>
              </div>
            )}

            <Button
              data-testid="back-home-button"
              onClick={() => navigate("/")}
              className="mt-8 rounded-full bg-cocoa px-8 py-6 text-white hover:bg-cocoadark"
            >
              Back to Home
            </Button>
          </div>
        )}

        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-error">
            <XCircle className="mx-auto h-14 w-14 text-destructive" />
            <h1 className="mt-6 font-heading text-3xl font-black text-ink">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-2 text-ashydark">
              {state === "timeout"
                ? "Your payment is taking longer than expected. Please check back shortly."
                : "Something went wrong with your payment. Please try again."}
            </p>
            <Button onClick={() => navigate("/checkout")} className="mt-6 rounded-full bg-cocoa px-8 py-6 text-white hover:bg-cocoadark">
              Return to checkout
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
